/**
 * The per-file-type upload MIME contract, **derived** from the backend's authoritative table.
 *
 * **This is the one place the frontend answers "what does this file type accept?"** Everything
 * that gates, advertises or validates an upload reads it from here: the drop-zone `accept`
 * presets, the per-slug `FILE_TYPE_METADATA` registries in `lib/services/{files,general-files}`,
 * the matter Add-document dialog's constraints, and the pre-upload validators.
 *
 * **Nothing below states a MIME type for a backend file type.** The lists themselves live in
 * `file-type-accept.generated.ts`, written by `scripts/generate-file-type-accept.mjs` from
 * `backend/src/files/constants/file-types.constants.ts` (VALE-1150). Before VALE-209 the same
 * facts were typed by hand in six places and had drifted in four of them; VALE-209 collapsed
 * them into one mirror and reconciled it with a test, which stopped the drift being silent but
 * still left a copy someone had to edit. This file is what remains once the copy is gone: the
 * parts of the contract the backend table genuinely does not know about.
 *
 * There are four of those, and each is here because it cannot be derived:
 *
 * 1. **Which slugs the frontend uploads against at all** ({@link UPLOADABLE_FILE_TYPE_SLUGS}) —
 *    a statement about our own surfaces, not about the backend. The generated table holds every
 *    backend type, including ones no user ever uploads (`aba-file`, `distribution-advice`,
 *    `ai-scribe-summary` are all written server-side).
 * 2. **The display-only aliases** ({@link DISPLAY_ONLY_ALIASES}) — registry keys that are labels
 *    for a file stored as some other type.
 * 3. **The client-intake unwrap exception** (see {@link EMAIL_UNWRAPPING_FILE_TYPES}).
 * 4. **The extension fallbacks** ({@link acceptStringFor}) — a browser-side problem the server
 *    never sees, because it sniffs the bytes.
 */

import {
  EMAIL_CONTAINER_MIME_TYPES,
  EMAIL_UNWRAPPING_FILE_TYPE_SLUGS,
  FILE_TYPE_ACCEPTED_MIME_TYPES
} from './file-type-accept.generated';

export { FILE_TYPE_ACCEPTED_MIME_TYPES };

/** Any slug in the backend's table, uploadable by our surfaces or not. */
export type BackendFileTypeSlug = keyof typeof FILE_TYPE_ACCEPTED_MIME_TYPES;

/**
 * The backend file types a frontend surface actually uploads against.
 *
 * Hand-authored on purpose, and the one list here that is a claim about *us* rather than about
 * the server: the generated table describes every type the backend defines, and most of the
 * difference is types written server-side that no picker should ever offer — an ABA file, a
 * distribution advice, an AI Scribe summary. Typing them as uploadable would let a drop-zone
 * preset declare one as its destination and no test would object.
 *
 * The annotation is what keeps it honest: a slug removed or renamed on the backend stops being
 * a `BackendFileTypeSlug` and fails the typecheck here, so this list cannot outlive the table.
 */
export const UPLOADABLE_FILE_TYPE_SLUGS = [
  // Matter and contact core slots — a scan or a photo of a single document.
  'primary-id',
  'secondary-id',
  'death-certificate',
  'will',
  'codicil',
  'grant-of-representation',
  'institution-form-signed',

  // General document surfaces.
  'correspondence-attachment',
  'instruction-sheet',
  'email-template-attachment',
  'additional-document',
  'ai-scribe-upload',
  'client-intake-upload',
  'lead-document',
  'unknown-pending-ai-analysis',

  // Firm configuration assets and generated-document doors.
  'institution-form-template',
  'letterhead',
  'handwritten-signature',
  'document-template',
  'distribution-declaration',
  'distribution-statement',
  'matter-precedent-document',
  'lead-cost-agreement',
  // A firm's trust account statement, uploaded against the Estate account (VALE-1718).
  'trust-account-statement',
  'imported-correspondence',
  'suggestion-attachment'
] as const satisfies readonly BackendFileTypeSlug[];

/** A backend file-type slug the frontend can upload against. */
export type UploadableFileTypeSlug =
  (typeof UPLOADABLE_FILE_TYPE_SLUGS)[number];

/**
 * Registry keys that are a **label**, not a file type — each one resolves to the backend type
 * the file behind it is actually stored as.
 *
 * Held as data rather than as a convention at each call site so the resolution is readable in
 * one place, and so {@link acceptedMimeTypesFor} can answer for an alias without every caller
 * having to know which real slug to ask about instead.
 *
 * `email-signature` is deliberately absent, and is the one registry key with no entry here: the
 * signature is authored in the app and stored as HTML on the user record, so there is no upload,
 * no backend `FileTypeSlug`, and nothing to alias it to. It keeps its label and icon in the
 * registries and states no accept contract at all.
 */
export const DISPLAY_ONLY_ALIASES = {
  /**
   * The document-type detector's fallback label (`lib/utils/file-type-detector.ts`). A file that
   * lands here is stored as an `additional-document`.
   */
  'matter-document': 'additional-document'
} as const satisfies Record<string, UploadableFileTypeSlug>;

/** A registry key that is a display label for a file stored as some other type. */
export type DisplayOnlyFileTypeAlias = keyof typeof DISPLAY_ONLY_ALIASES;

/**
 * The file types that unwrap a forwarded email instead of storing it.
 *
 * Mostly derived — {@link EMAIL_UNWRAPPING_FILE_TYPE_SLUGS} is generated from the backend's
 * `allowsEmailAttachmentExtraction` flag. These accept a `.eml`/`.msg` at the door **without**
 * listing the email MIME types, because `FilesService.tryUnwrapEmailContainer` runs before MIME
 * validation and replaces the wrapper with its attachments. So the set a picker may offer is
 * wider than the set the validator checks, and the two must not be conflated: offering an email
 * on a type that does not unwrap (`secondary-id`, say) only moves the refusal to the server.
 *
 * `client-intake-upload` is added by hand because it is not derivable. The portal runs its OWN
 * unwrap (`PortalIntakeFormService.completeUpload`, gated on `portalDocumentType === 'other'`),
 * so the backend file type carries no flag to generate from. Only the portal's generic
 * "additional documents" slot offers an email; its typed slots (death certificate, ID, will) do
 * not, and the server refuses one there with a message naming the slot.
 */
export const EMAIL_UNWRAPPING_FILE_TYPES: readonly string[] = [
  ...EMAIL_UNWRAPPING_FILE_TYPE_SLUGS,
  'client-intake-upload'
];

/**
 * Extensions per MIME type, for the two places a MIME type alone is not enough: the `accept`
 * attribute (a picker cannot match a format the browser labels blank) and the pre-upload
 * fallback check.
 *
 * Frontend-owned, and the one part of the contract that genuinely is: the server sniffs the
 * bytes and never reads an extension, so there is nothing on the backend to derive this from.
 * It is applied by lookup rather than by listing, so an extension can never outlive the MIME
 * type that justified it — `.gif` and `.txt` sat in a hand-kept fallback list long after the
 * formats were dropped, which let a GIF past the client and into a server rejection (VALE-209).
 */
const EXTENSIONS_BY_MIME_TYPE: Record<string, readonly string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx'
  ],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx'
  ],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'text/plain': ['.txt'],
  'message/rfc822': ['.eml'],
  'application/vnd.ms-outlook': ['.msg'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/webm': ['.webm'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'application/x-7z-compressed': ['.7z']
};

/**
 * MIME types that belong in a **validator** set but never in an `accept` attribute.
 *
 * Both are what the server's magic-number sniffer calls an email container, not what a browser
 * ever puts on `file.type`. `application/octet-stream` is the dangerous one: a picker filtered on
 * it matches every file the OS cannot label, which is most of them — so deriving the `accept`
 * string straight from the uploadable set would quietly turn the ID and correspondence pickers
 * into "all files". They stay in {@link uploadableMimeTypesFor} because a real `.msg` genuinely
 * arrives under both, and the pre-upload check must let it through.
 *
 * The `.eml`/`.msg` extensions in {@link MIME_TYPES_NEEDING_EXTENSION_FALLBACK} are what actually
 * makes an email pickable, and they are unaffected by this.
 */
const MIME_TYPES_EXCLUDED_FROM_ACCEPT = [
  'application/octet-stream',
  'application/x-cfb'
];

/**
 * Named format groups, for a surface that deliberately offers **less** than its destination type
 * accepts (see `narrowTo` on {@link acceptStringForSlugs}).
 *
 * These name a *format*, not a file type's contract — "a PDF is `application/pdf`" is not a claim
 * about the backend and cannot drift from it, which is why they can live here while the per-type
 * lists cannot. Call sites narrow by group name so no drop-zone preset has to write a MIME string
 * to say "PDFs and photos only".
 */
export const ACCEPT_FORMAT_GROUPS = {
  pdf: ['application/pdf'],
  word: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword'
  ],
  /** `.docx` alone — the modern format, without its legacy `.doc` sibling. */
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  spreadsheet: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ],
  /** A scan or a photo, as a desktop browser labels it. */
  image: ['image/jpeg', 'image/jpg', 'image/png'],
  /** What an iPhone camera produces by default, transcoded to JPEG on ingest. */
  phonePhoto: ['image/heic', 'image/heif'],
  /** Every shape a forwarded `.eml`/`.msg` arrives as. */
  email: EMAIL_CONTAINER_MIME_TYPES,
  text: ['text/plain']
} as const satisfies Record<string, readonly string[]>;

/** Flatten `groups` into the MIME types they name, for a `narrowTo` argument. */
export function formatGroups(
  ...groups: readonly (keyof typeof ACCEPT_FORMAT_GROUPS)[]
): string[] {
  return groups.flatMap((group) => [...ACCEPT_FORMAT_GROUPS[group]]);
}

/**
 * The formats whose browser-reported MIME type cannot be relied on, so an `accept` list needs
 * their extension too: `.heic`/`.heif` frequently arrive with an empty `file.type`, and
 * `.eml`/`.msg` frequently arrive as `application/octet-stream` (Windows without Outlook
 * registered, most Linux browsers).
 */
const MIME_TYPES_NEEDING_EXTENSION_FALLBACK = [
  'image/heic',
  'image/heif',
  'message/rfc822',
  'application/vnd.ms-outlook'
];

/** The real backend slug behind `slug`, resolving a display-only alias to what it stands for. */
function resolveSlug(slug: string): string {
  return (
    (DISPLAY_ONLY_ALIASES as Record<string, string | undefined>)[slug] ?? slug
  );
}

/**
 * What `slug` accepts, as a plain mutable array for the `string[]` shapes that consume it.
 *
 * A display-only alias resolves to the type its file is stored as. Anything else unknown —
 * `email-signature`, a typo — returns an empty list rather than throwing, so the caller's own
 * validation reports it.
 */
export function acceptedMimeTypesFor(slug: string): string[] {
  const entry = (
    FILE_TYPE_ACCEPTED_MIME_TYPES as Record<
      string,
      readonly string[] | undefined
    >
  )[resolveSlug(slug)];
  return entry ? [...entry] : [];
}

/**
 * Everything a user may legitimately *offer* for `slug`: what the backend validates, plus the
 * email container formats when the type unwraps one.
 *
 * This, not {@link acceptedMimeTypesFor}, is what a picker or a pre-upload check should use —
 * the two differ only for the unwrapping types, and only by the email formats.
 */
export function uploadableMimeTypesFor(slug: string): string[] {
  const resolved = resolveSlug(slug);
  const accepted = acceptedMimeTypesFor(resolved);
  if (
    accepted.length === 0 ||
    !EMAIL_UNWRAPPING_FILE_TYPES.includes(resolved)
  ) {
    return accepted;
  }
  return [
    ...accepted,
    ...EMAIL_CONTAINER_MIME_TYPES.filter((mime) => !accepted.includes(mime))
  ];
}

/**
 * The lowercase extensions (`.pdf`, `.docx`, …) `slug` accepts, for the fallback check when a
 * browser reports an empty or generic `file.type`.
 */
export function acceptedExtensionsFor(slug: string): string[] {
  const extensions = uploadableMimeTypesFor(slug).flatMap(
    (mime) => EXTENSIONS_BY_MIME_TYPE[mime] ?? []
  );
  return Array.from(new Set(extensions));
}

/**
 * Append the extension fallbacks `mimeTypes` needs, drop the ones no picker should filter on,
 * and join into an `accept` string.
 */
function toAcceptString(mimeTypes: readonly string[]): string {
  const pickable = mimeTypes.filter(
    (mime) => !MIME_TYPES_EXCLUDED_FROM_ACCEPT.includes(mime)
  );

  const extensions = pickable
    .filter((mime) => MIME_TYPES_NEEDING_EXTENSION_FALLBACK.includes(mime))
    .flatMap((mime) => EXTENSIONS_BY_MIME_TYPE[mime] ?? []);

  return [...pickable, ...Array.from(new Set(extensions))].join(',');
}

/**
 * The `accept` attribute string for `slug`, for a native file picker.
 *
 * Extensions are appended only for {@link MIME_TYPES_NEEDING_EXTENSION_FALLBACK} — the formats
 * an `accept` list of MIME types alone silently fails to match.
 */
export function acceptStringFor(slug: string): string {
  return toAcceptString(uploadableMimeTypesFor(slug));
}

/**
 * The `accept` attribute string for a surface that uploads to **several** destinations.
 *
 * The result is the intersection of what every destination accepts, because a shared surface can
 * only honestly offer what all of its destinations take. That is exactly what had gone wrong
 * before VALE-209: one preset offered HEIC to four destination types while only two accepted it,
 * so an iPhone photo passed the picker, passed every client-side check, and was refused after
 * the S3 PUT on two of the four surfaces.
 *
 * `narrowTo` restricts it further, for a surface that deliberately offers **less** than its
 * destinations would take — a Word-template door accepts only `.docx` from a type that would
 * also take a PDF, because the file is rendered through docxtemplater and a PDF has nothing to
 * render. Narrowing is always safe (the server would accept more); widening is what this
 * function exists to make impossible, so a `narrowTo` entry no destination accepts is dropped
 * rather than honoured.
 */
export function acceptStringForSlugs(
  slugs: readonly string[],
  narrowTo?: readonly string[]
): string {
  if (slugs.length === 0) return '';

  const [first, ...rest] = slugs.map((slug) => uploadableMimeTypesFor(slug));
  const shared = (first ?? []).filter((mime) =>
    rest.every((mimeTypes) => mimeTypes.includes(mime))
  );

  return toAcceptString(
    narrowTo ? shared.filter((mime) => narrowTo.includes(mime)) : shared
  );
}
